/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React, { useRef } from "react";

import { motion } from "framer-motion";
import styled from "styled-components";
import { useOnClickOutside } from "usehooks-ts";

import { Layer } from "@vienna/common";

import { HOTKEYS } from "src/components/Hotkeys/hotkeys";
import { StandardButton } from "src/components/UI/Buttons/StandardButton";
import { useHotkey } from "src/lib/hooks/useHotkey";

interface AlertProps {
  type: "question" | "notification";
  isOpen: boolean;
  headerText: string;
  bodyText?: string;
  /* -------------- Accept/Decline Alert Type ------------- */
  acceptText?: string;
  declineText?: string;
  onAccept?: (e?: React.MouseEvent | undefined) => void;
  onDecline?: (e?: React.MouseEvent | undefined) => void;
  declineDangerous?: boolean; // whether the decline button should be red
  /* -------------- Notification Alert Type ------------- */
  okayText?: string;
  onOkay?: (e?: React.MouseEvent | undefined) => void;
  /* -------------- Close Options ------------- */
  closeWithESC?: boolean;
  closeWithOutsideClick?: boolean;
  closeWithEnter?: boolean;
  className?: string;
  children?: React.ReactNode;
}

export const Alert = ({
  className,
  headerText,
  bodyText,
  okayText,
  onOkay,
  acceptText,
  declineText,
  onAccept,
  onDecline,
  declineDangerous = false,
  type,
  isOpen,
  closeWithESC,
  closeWithOutsideClick,
  closeWithEnter,
  children,
}: AlertProps) => {
  const alertRef = useRef<HTMLDivElement>(null);

  const safeHeaderText = typeof headerText === "string" ? headerText : "Something went wrong";
  const safeBodyText = typeof bodyText === "string" ? bodyText : "";

  useHotkey(HOTKEYS.ESCAPE, () => {
    if (closeWithESC && isOpen) {
      onDecline ? onDecline() : onOkay && onOkay();
    }
  });

  useHotkey(HOTKEYS.ENTER, () => {
    if (closeWithEnter && isOpen) {
      onAccept ? onAccept() : onOkay && onOkay();
    }
  });

  useOnClickOutside(alertRef, () => {
    if (closeWithOutsideClick && isOpen) {
      onDecline ? onDecline() : onOkay && onOkay();
    }
  });

  return (
    <>
      {isOpen && (
        <BackgroundOverlay>
          <StyledAlert className={className} ref={alertRef}>
            <ModalHeader>{safeHeaderText}</ModalHeader>
            {safeBodyText && <ModalBody>{safeBodyText}</ModalBody>}
            {children && <>{children}</>}
            <ModalFooter id="cy-alert-options">
              {type === "question" && onDecline && onAccept ? (
                <>
                  <StandardButton
                    onClick={onDecline}
                    highContrast={!declineDangerous}
                    dangerous={declineDangerous}
                    styles={{ minWidth: "96px" }}
                  >
                    {declineText}
                  </StandardButton>
                  <StandardButton onClick={onAccept} styles={{ minWidth: "96px", marginLeft: "25px" }}>
                    {acceptText}
                  </StandardButton>
                </>
              ) : (
                onOkay && <StandardButton onClick={onOkay}>{okayText}</StandardButton>
              )}
            </ModalFooter>
          </StyledAlert>
        </BackgroundOverlay>
      )}
    </>
  );
};

/**TODO: abstract this for all modals */
const BackgroundOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${Layer.OVERLAY};
  background-color: rgba(131, 131, 131, 0.304);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const StyledAlert = styled(motion.div)`
  position: relative;
  top: -40px;
  width: 500px;
  padding: 30px;
  margin-left: auto;
  margin-right: auto;
  background-color: ${({ theme }) => theme.background};
  border: 2px solid #494949;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: ${({ theme }) => `10px 10px ${theme.subtleContrast}`};
  font-family: "IBM Plex Mono";
  color: ${({ theme }) => theme.text.default};
  overflow: hidden;
  transform-origin: top center;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  width: 100%;
`;

const ModalHeader = styled.h1`
  width: 100%;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
`;

const ModalBody = styled.p`
  width: 100%;
  font-weight: 300;
  font-size: 14px;
  margin-top: 15px;
`;
